import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { directoryApi, type GridView, type GridViews } from '../client';
import type { useGridLayout } from './useGridLayout';

/** Stable query-key factory for grid views. */
export const gridViewKeys = {
  all: ['directory', 'grid-view'] as const,
  byGrid: (organisationId: string, app: string, gridKey: string) =>
    ['directory', 'grid-view', organisationId, app, gridKey] as const,
};

const selectedKey = (organisationId: string, app: string, gridKey: string) =>
  `my.gridView.${organisationId}.${app}.${gridKey}`;

function readSelected(key: string | null): string | null {
  if (!key) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSelected(key: string | null, id: string | null) {
  if (!key) return;
  try {
    if (id) window.localStorage.setItem(key, id);
    else window.localStorage.removeItem(key);
  } catch {
    // Private window — the choice just isn't remembered.
  }
}

/**
 * A grid's named views: the shared ones and mine, which one is on screen, and
 * whether the screen has moved away from it ("modified").
 *
 * <p>
 * A view is a snapshot of the columns only (through `layout`): which show,
 * their order and their widths. Never the screen's filters or sort — those
 * are the screen's own to remember, so changing a filter never marks a view
 * "modified" and picking a view never changes one. Picking a view copies its
 * columns onto the screen; nothing stays linked. The last view picked is
 * remembered per grid in this browser.
 * </p>
 */
export function useGridViews({
  organisationId,
  app,
  gridKey,
  layout,
}: {
  organisationId: string | null;
  app: string;
  gridKey: string;
  layout: ReturnType<typeof useGridLayout>;
}) {
  const qc = useQueryClient();
  const key = useMemo(
    () => (organisationId ? gridViewKeys.byGrid(organisationId, app, gridKey) : gridViewKeys.all),
    [organisationId, app, gridKey],
  );
  const storageKey = organisationId ? selectedKey(organisationId, app, gridKey) : null;

  const query = useQuery<GridViews, Error>({
    queryKey: key,
    queryFn: () => directoryApi.getGridViews(organisationId!, app, gridKey),
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
  });
  const views = useMemo(() => query.data?.views ?? [], [query.data]);

  const [selectedId, setSelectedIdState] = useState<string | null>(() => readSelected(storageKey));
  const setSelectedId = useCallback(
    (id: string | null) => {
      setSelectedIdState(id);
      writeSelected(storageKey, id);
    },
    [storageKey],
  );
  const selected = views.find((v) => v.id === selectedId) ?? null;

  const dirty =
    !!selected && layout.resolvedLayoutJson(selected.layoutJson) !== layout.currentLayoutJson;

  const apply = useCallback(
    (view: GridView) => {
      layout.applyLayoutJson(view.layoutJson);
      setSelectedId(view.id);
    },
    [layout, setSelectedId],
  );

  // Once the list first arrives, forget a remembered view that has since been
  // deleted. The columns themselves are already on screen — the layout keeps
  // them — so nothing is re-applied.
  const appliedOnLoad = useRef(false);
  useEffect(() => {
    if (appliedOnLoad.current || !query.isSuccess) return;
    appliedOnLoad.current = true;
    if (!selectedId) return;
    const remembered = views.find((v) => v.id === selectedId);
    if (!remembered) setSelectedId(null);
  }, [query.isSuccess, views, selectedId, setSelectedId]);

  // ── Saving ─────────────────────────────────────────────────────────────────
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(
    async <T>(work: () => Promise<T>, fallback: string): Promise<T | undefined> => {
      setBusy(true);
      setError(null);
      try {
        return await work();
      } catch (err) {
        setError(err instanceof Error ? err.message : fallback);
        return undefined;
      } finally {
        setBusy(false);
        void qc.invalidateQueries({ queryKey: key });
      }
    },
    [qc, key],
  );

  /** Save what's on screen under a new name; it becomes the selected view. Resolves true on success. */
  const saveAsNew = useCallback(
    async (name: string, shared: boolean) => {
      const created = await run(
        () =>
          directoryApi.createGridView({
            organisationId: organisationId!,
            app,
            gridKey,
            name: name.trim(),
            shared,
            layoutJson: layout.currentLayoutJson,
            // Columns only; the column is kept for older rows.
            stateJson: '{}',
          }),
        'Couldn’t save the view.',
      );
      if (created) setSelectedId(created.id);
      return !!created;
    },
    [run, organisationId, app, gridKey, layout.currentLayoutJson, setSelectedId],
  );

  /** Save what's on screen over the selected view. */
  const updateSelected = useCallback(async () => {
    if (!selected) return false;
    const saved = await run(
      () =>
        directoryApi.updateGridView({
          viewId: selected.id,
          name: selected.name,
          layoutJson: layout.currentLayoutJson,
          stateJson: '{}',
        }),
      'Couldn’t update the view.',
    );
    return !!saved;
  }, [run, selected, layout.currentLayoutJson]);

  const rename = useCallback(
    async (id: string, name: string) =>
      !!(await run(
        () => directoryApi.updateGridView({ viewId: id, name: name.trim() }),
        'Couldn’t rename the view.',
      )),
    [run],
  );

  const remove = useCallback(
    async (id: string) => {
      const done = await run(() => directoryApi.deleteGridView(id), 'Couldn’t delete the view.');
      if (done !== undefined && id === selectedId) setSelectedId(null);
      return done !== undefined;
    },
    [run, selectedId, setSelectedId],
  );

  return {
    views,
    canShare: query.data?.canShare ?? false,
    isLoading: query.isPending && !!organisationId,
    /** The view on screen, or null. */
    selected,
    /** The screen has changed since the view was picked or saved. */
    dirty,
    /** Put a view's columns on screen. */
    select: (id: string) => {
      const view = views.find((v) => v.id === id);
      if (view) apply(view);
    },
    /** Stop showing a view name — the screen stays as it is. */
    clear: () => setSelectedId(null),
    saveAsNew,
    updateSelected,
    rename,
    remove,
    busy,
    error,
  };
}
